import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import { DataMappingDeactivateWarningService } from '@features/data-mapping/services/data-mapping-deactivate-warning.service';
import {select, Store} from '@ngrx/store';
import {IToastState} from '@shared/store/toast/toast-reducer';
import {getNotification} from '@shared/store/toast/toast-selectors';
import {merge, Subject} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';

@Component({
    standalone: false,
    selector: 'app-edit-data-mapping',
  templateUrl: './edit-data-mapping.component.html',
  styleUrls: ['./edit-data-mapping.component.scss']
})
export class EditDataMappingComponent implements OnInit, OnDestroy, OnChanges {
  @Input() magicLinkModel: MagicLinkModel;
  @Input() handler: IPlatformToolHandler;
  @Input() selectedTab: TabModel;

  api: string;
  sourceType: string;
  serviceName: string;
  sortFieldKey: string;
  sortDirection: string;

  private readonly destroy$ = new Subject<void>();
  private readonly informationCounterResubscribe$ = new Subject<void>();

  constructor(
    private dataMappingDeactivateWarningService: DataMappingDeactivateWarningService,
    private toastStore: Store<IToastState>
  ) {
  }

  ngOnInit(): void {
    const tabData = this.magicLinkModel.tabBar.tabs[this.magicLinkModel.tabBar.selectedIndex].tabData;
    this.sourceType = tabData['SourceType'] ?? 'DataMappingFieldItems';
    this.api = tabData['Api'];
    this.serviceName = tabData['ServiceName'];
    this.sortFieldKey = tabData['SortFieldKey'] ?? '';
    this.sortDirection = tabData['SortDirection'] ?? '';
    this.updatePrimaryCountInService();
    this.watchInformationCounterNotifications();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['magicLinkModel'] && this.magicLinkModel) {
      this.updatePrimaryCountInService();
      if (!changes['magicLinkModel'].firstChange) {
        this.informationCounterResubscribe$.next();
        this.watchInformationCounterNotifications();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.informationCounterResubscribe$.complete();
    this.dataMappingDeactivateWarningService.clear();
    this.handler.destroy();
  }

  private watchInformationCounterNotifications(): void {
    const counterId = this.magicLinkModel?.informationBar?.informationCounterId;
    if (!counterId) {
      return;
    }
    this.toastStore
      .pipe(
        select(getNotification, counterId),
        filter((notification: any) => !!notification?.payload?.counter),
        takeUntil(merge(this.destroy$, this.informationCounterResubscribe$))
      )
      .subscribe(() => this.updatePrimaryCountInService());
  }

  private updatePrimaryCountInService(): void {
    const parseBarCountByKey = (key: string): number => {
      const cmd = this.magicLinkModel?.informationBar?.commands?.find(
        (c) => c.fieldValues?.['Key'] === key
      );
      const textValue = cmd?.fieldValues?.['Text'];
      const n = textValue !== undefined && textValue !== null ? Number(textValue) : NaN;
      return Number.isNaN(n) ? 0 : n;
    };
    const primary = parseBarCountByKey('Primary');
    const combinedOther =
      parseBarCountByKey('Unmapped') + parseBarCountByKey('Mapped') + parseBarCountByKey('Aggregate');
    this.dataMappingDeactivateWarningService.setPrimaryCount(primary);
    this.dataMappingDeactivateWarningService.setUnmappedMappedAggregateTotal(combinedOther);
  }

  onSelectionChanged($event: any) {
    this.handler.selectionChanged($event);
    this.updatePrimaryCountInService();
  }

  onGridCellValueChange($event: any) {
    this.handler.valueChanged($event);
    this.updatePrimaryCountInService();
  }

  onColumnHeaderButtonClicked($event: any) {
    this.handler.selectionChanged($event);
    this.updatePrimaryCountInService();
  }
}
